import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Bell, ArrowLeftRight, Plane, Home, CheckCheck, Trash2, Clock } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (showDropdown) fetchNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        bellRef.current && !bellRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showDropdown && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: Math.max(8, rect.left - 40),
      });
    }
  }, [showDropdown]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data || []);
    } catch {}
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadCount(response.data?.unread_count || 0);
    } catch {}
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch {
      toast.error('Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const clearRead = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      await axios.delete(`${API}/notifications/clear-read`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.filter(n => !n.read));
      toast.success('Read notifications cleared');
    } catch {
      toast.error('Failed to clear');
    } finally {
      setLoading(false);
    }
  };

  const dismissNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const n = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (n && !n.read) setUnreadCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const handleClick = (notification) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.type === 'pto_request') navigate('/pto');
    else if (notification.type === 'wfh_request') navigate('/pto');
    else if (notification.type === 'shift_swap') navigate('/shift-swaps');
    setShowDropdown(false);
  };

  const icon = (type) => {
    if (type === 'pto_request') return <Plane className="h-4 w-4 text-violet-500" />;
    if (type === 'wfh_request') return <Home className="h-4 w-4 text-cyan-500" />;
    if (type === 'shift_swap') return <ArrowLeftRight className="h-4 w-4 text-orange-500" />;
    return <Bell className="h-4 w-4 text-slate-400" />;
  };

  const label = (type) => {
    if (type === 'pto_request') return 'Time Off';
    if (type === 'wfh_request') return 'Work From Home';
    if (type === 'shift_swap') return 'Shift Swap';
    return 'Notification';
  };

  const labelColor = (type) => {
    if (type === 'pto_request') return 'bg-violet-50 text-violet-700';
    if (type === 'wfh_request') return 'bg-cyan-50 text-cyan-700';
    if (type === 'shift_swap') return 'bg-orange-50 text-orange-700';
    return 'bg-slate-50 text-slate-600';
  };

  const timeAgo = (dateString) => {
    const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  const unread = notifications.filter(n => !n.read);
  const read = notifications.filter(n => n.read);

  const dropdown = showDropdown && createPortal(
    <div
      ref={dropdownRef}
      data-testid="notification-dropdown"
      className="fixed z-[9999] w-[420px] max-h-[520px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
      style={{ top: dropdownPos.top, left: dropdownPos.left }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{unreadCount} new</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead} disabled={loading}
              className="text-xs h-7 text-slate-500 hover:text-slate-900 gap-1" data-testid="mark-all-read-button">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
          {read.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearRead} disabled={loading}
              className="text-xs h-7 text-slate-400 hover:text-red-600 gap-1" data-testid="clear-read-button">
              <Trash2 className="h-3.5 w-3.5" /> Clear read
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto max-h-[440px]">
        {notifications.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Bell className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500">No notifications</p>
          </div>
        ) : (
          <>
            {/* Unread Section */}
            {unread.length > 0 && (
              <>
                <div className="px-4 py-1.5 bg-blue-50/50 border-b border-blue-100">
                  <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide">Needs Attention ({unread.length})</span>
                </div>
                {unread.map((n) => (
                  <div key={n.id} data-testid={`notification-item-${n.id}`}
                    className="px-4 py-3 cursor-pointer transition-colors border-b border-slate-50 hover:bg-blue-50/30 bg-blue-50/20"
                    onClick={() => handleClick(n)}>
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">{icon(n.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${labelColor(n.type)}`}>{label(n.type)}</span>
                          <div className="h-1.5 w-1.5 bg-blue-600 rounded-full" />
                          <span className="text-[11px] text-slate-400 ml-auto flex-shrink-0">{timeAgo(n.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
                      </div>
                      <button onClick={(e) => dismissNotification(n.id, e)}
                        className="flex-shrink-0 p-1 text-slate-300 hover:text-red-500 rounded transition-colors" title="Dismiss">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Read Section */}
            {read.length > 0 && (
              <>
                <div className="px-4 py-1.5 bg-slate-50/80 border-b border-slate-100">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Earlier</span>
                </div>
                {read.map((n) => (
                  <div key={n.id} data-testid={`notification-item-${n.id}`}
                    className="px-4 py-2.5 cursor-pointer transition-colors border-b border-slate-50 hover:bg-slate-50 opacity-70"
                    onClick={() => handleClick(n)}>
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">{icon(n.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${labelColor(n.type)}`}>{label(n.type)}</span>
                          <span className="text-[11px] text-slate-400 ml-auto flex-shrink-0">{timeAgo(n.created_at)}</span>
                        </div>
                        <p className="text-xs text-slate-500 leading-snug">{n.message}</p>
                      </div>
                      <button onClick={(e) => dismissNotification(n.id, e)}
                        className="flex-shrink-0 p-1 text-slate-200 hover:text-red-500 rounded transition-colors" title="Dismiss">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <Button
        ref={bellRef}
        variant="ghost"
        size="icon"
        className="relative"
        data-testid="notification-bell"
        onClick={() => {
          setShowDropdown(!showDropdown);
          if (!showDropdown) fetchNotifications();
        }}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 bg-red-500 text-white text-[10px]">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>
      {dropdown}
    </>
  );
};

export default NotificationBell;
